import React, { useEffect, useRef, useState } from 'react';
import { useApplications, useAsync } from '../hooks/usePortal';
import { portal, uploadDocument, uploadQueue, downloadDocument, fetchDocumentObject } from '../lib/portal';
import { artifactDownloadUrl, languageDisplayName, CbcPackage } from '../lib/cbcTranslation';
import { useOnline } from '../lib/net';
import { track } from '../lib/analytics';
import OfficialDocumentRow from '../components/OfficialDocumentRow';

/**
 * Documents (AP-7): checklist with the full state machine (missing → pending
 * review → approved / rejected → resubmit), version history per document
 * type, drag-and-drop upload with client-side image compression, offline
 * queueing (AP-16), and download of official program documents.
 */
/** Read-only mode for now: hide the applicant upload box. */
const UPLOAD_ENABLED = false;

const BADGE: Record<string, { cls: string; label: string }> = {
  approved: { cls: 'ok', label: 'Approved' },
  accepted: { cls: 'ok', label: 'Approved' },
  pending: { cls: 'warn', label: 'Pending review' },
  uploaded: { cls: 'warn', label: 'Pending review' },
  in_review: { cls: 'warn', label: 'In review' },
  rejected: { cls: 'err', label: 'Rejected — resubmit' },
  missing: { cls: 'muted', label: 'Not uploaded' },
};

type DisplayDocument = {
  id: string;
  type: string;
  name: string;
  status: string;
  url?: string;
  official: boolean;
  rejectionReason?: string;
  uploadedAt?: string;
  version: number;
};

type PortalDocumentPayload = {
  id?: unknown;
  documentType?: unknown;
  fileName?: unknown;
  status?: unknown;
  downloadUrl?: unknown;
  official?: unknown;
  rejectionReason?: unknown;
  createdAt?: unknown;
  version?: unknown;
};

function isVerifiedDs2019(document: Pick<DisplayDocument, 'type' | 'status'>): boolean {
  return document.type === 'ds-2019' && document.status === 'verified';
}

function isAvailableDocument(document: DisplayDocument): boolean {
  if (document.type !== 'ds-2019') return true;
  return isVerifiedDs2019(document);
}

export default function DocumentsPage() {
  const { active, loading, error } = useApplications();
  const docs = useAsync(
    () => (active ? portal.documents(active.id) : Promise.resolve([])),
    [active?.id, active?.appUpdatedAt, active?.status],
  );
  // Hub translation pipeline state (criminal background etc.): pairs an
  // uploaded original with its AI-generated English translation. Best-effort —
  // a failure here must never break the documents list.
  const cbc = useAsync(
    () =>
      active
        ? portal.criminalBackgroundStatus(active.id).then((r) => (r?.packages ?? []) as CbcPackage[], () => [] as CbcPackage[])
        : Promise.resolve([] as CbcPackage[]),
    [active?.id],
  );

  // In-page inline preview of a selected document (rendered in the main content
  // area, not a new tab). Hooks stay ABOVE the early returns — this component
  // has conditional returns and hook order must never change (React #310).
  const [preview, setPreview] = useState<{ name: string; url: string; type: string } | null>(null);
  const [previewBusy, setPreviewBusy] = useState<string | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // Downloads are async and can fail server-side (409/502 — e.g. the terms
  // certificate refusing to certify). Without a catch the click is a silent
  // no-op + unhandled rejection; surface it in the same alert style as preview.
  const download = (path: string, name: string) => {
    setPreviewErr(null);
    downloadDocument(path, name).catch((e) =>
      setPreviewErr(`Couldn't download ${name}: ${(e as Error).message}`),
    );
  };

  const revokePreview = () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
  };
  const showInline = async (path: string, name: string) => {
    setPreviewErr(null);
    setPreviewBusy(path);
    try {
      const { url, type } = await fetchDocumentObject(path);
      revokePreview();
      previewUrlRef.current = url;
      setPreview({ name, url, type });
    } catch (e) {
      setPreviewErr(`Couldn't open ${name}: ${(e as Error).message}`);
    } finally {
      setPreviewBusy(null);
    }
  };
  const closePreview = () => {
    revokePreview();
    setPreview(null);
  };
  const showDocument = (document: Pick<DisplayDocument, 'url' | 'name'>) => {
    if (!document.url) return;
    void showInline(document.url, document.name);
  };
  const downloadDisplayDocument = (document: Pick<DisplayDocument, 'url' | 'name'>) => {
    if (!document.url) return;
    download(document.url, document.name);
  };
  // Revoke the object URL on unmount so the blob is not leaked.
  useEffect(() => revokePreview, []);

  if (loading || docs.loading) return <div className="card">Loading documents…</div>;
  if (error || docs.error) return <div className="card" role="alert"><p>{error ?? docs.error}</p></div>;
  if (!active) return <div className="card"><h1>Documents</h1><p>Start an application from your dashboard first.</p></div>;

  const portalDocuments = (docs.data ?? []) as PortalDocumentPayload[];
  const all = portalDocuments.map((d): DisplayDocument => ({
    id: String(d.id ?? ''),
    type: String(d.documentType ?? 'document').toLowerCase(),
    name: typeof d.fileName === 'string' ? d.fileName : 'file',
    status: String(d.status ?? 'pending').toLowerCase(),
    url: typeof d.downloadUrl === 'string' ? d.downloadUrl : undefined,
    official: Boolean(d.official),
    rejectionReason: typeof d.rejectionReason === 'string' ? d.rejectionReason : undefined,
    uploadedAt: typeof d.createdAt === 'string' ? d.createdAt : undefined,
    version: typeof d.version === 'number' ? d.version : 1,
  })).filter(isAvailableDocument);

  // Version history: group by type; newest first is the live version.
  const byType = new Map<string, typeof all>();
  all.filter((d) => !d.official).forEach((d) => byType.set(d.type, [...(byType.get(d.type) ?? []), d]));
  byType.forEach((v) => v.sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt))));
  const ds2019 = all.find(isVerifiedDs2019);
  const official = all.filter((d) => d.official && d.type !== 'ds-2019');
  const agreements = official.filter((d) => ['signed-agreement', 'terms-acceptance'].includes(d.type));
  const otherOfficial = official.filter((d) => !['signed-agreement', 'terms-acceptance'].includes(d.type));
  const officialRow = (document: DisplayDocument) => (
    <OfficialDocumentRow
      key={document.id}
      document={document}
      signedOn={document.type === 'signed-agreement' ? formatSignedOn(document.uploadedAt) : undefined}
      opening={previewBusy === document.url}
      onView={() => showDocument(document)}
      onDownload={() => downloadDisplayDocument(document)}
    />
  );

  // English-translation children keyed by the original upload's file name
  // (the status payload has no shared document id with the documents list).
  const translationsByFileName = new Map<
    string,
    { url: string; language: string; status: string }
  >();
  for (const pkg of cbc.data ?? []) {
    const rec = pkg.record;
    const name = rec?.original?.fileName;
    if (!rec || !name || !rec.translationRequired) continue;
    const url = artifactDownloadUrl(rec.translation);
    translationsByFileName.set(name, {
      url: url ?? '',
      language: languageDisplayName(rec.detectedLanguage),
      status: rec.processingStatus,
    });
  }

  return (
    <>
      <h1 style={{ marginBottom: 10 }}>Documents</h1>
      {/* Applicant upload is disabled for now — this page is read-only. It lists
          the documents you have already uploaded (with a View link for each) and
          any official documents issued to you. Flip UPLOAD_ENABLED to re-enable
          the drag-and-drop uploader. */}
      {UPLOAD_ENABLED && <Uploader appId={active.id} onDone={docs.reload} />}

      {previewErr && <div className="card" role="alert"><p>{previewErr}</p></div>}

      {preview && (
        <div className="card docpreview">
          <div className="docpreview-head">
            <strong>{preview.name}</strong>
            <span className="docpreview-actions">
              <a href={preview.url} download={preview.name}>Download</a>
              <button type="button" onClick={closePreview}>Close</button>
            </span>
          </div>
          <div className="docpreview-body">
            {preview.type.startsWith('image/') ? (
              <img src={preview.url} alt={preview.name} />
            ) : preview.type === 'application/pdf' ? (
              <iframe title={preview.name} src={preview.url} />
            ) : (
              <p>
                This file type can't be previewed here.{' '}
                <a href={preview.url} download={preview.name}>Download it</a> to open it.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <h2>Documents you've uploaded</h2>
        {byType.size === 0 && <p>You haven't uploaded any documents to this application yet.</p>}
        {[...byType.entries()].map(([type, versions]) => {
          const head = versions[0];
          const badge = BADGE[head.status] ?? BADGE.pending;
          return (
            <div key={type} className="docrow">
              <div className="docmain">
                <strong>{head.name}</strong> <span className={`pill ${badge.cls}`}>{badge.label}</span>
                <div className="sub muted">
                  {prettyType(type)}
                  {head.uploadedAt ? ` · Uploaded ${fmtDate(head.uploadedAt)}` : ''}
                </div>
                {head.status === 'rejected' && head.rejectionReason && (
                  <div className="sub globus-msg" role="alert">{head.rejectionReason}</div>
                )}
                {(() => {
                  const tr = translationsByFileName.get(head.name);
                  if (!tr) return null;
                  if (tr.url) {
                    const trName = `${head.name.replace(/\.[^.]+$/, '')}_English_Translation.pdf`;
                    return (
                      <div className="sub">
                        Translated from {tr.language} ·{' '}
                        <a href="#" onClick={(e) => { e.preventDefault(); void showInline(tr.url, trName); }}>View English translation</a>
                        {' · '}
                        <a href="#" onClick={(e) => { e.preventDefault(); download(tr.url, trName); }}>Download</a>
                      </div>
                    );
                  }
                  if (tr.status === 'failed' || tr.status === 'manual_intervention_required') {
                    return <div className="sub muted">Automatic English translation unavailable — our team will follow up.</div>;
                  }
                  return <div className="sub muted">Translating from {tr.language} to English…</div>;
                })()}
                {versions.length > 1 && (
                  <details className="history">
                    <summary>Earlier versions ({versions.length - 1})</summary>
                    <ul>
                      {versions.slice(1).map((v, i) => (
                        <li key={v.id ?? i}>
                          v{versions.length - 1 - i} · {v.name} · {fmtDate(v.uploadedAt)} · {(BADGE[v.status] ?? BADGE.pending).label}
                          {v.url && (
                            <> · <a href="#" onClick={(e) => { e.preventDefault(); showDocument(v); }}>view</a>
                            {' · '}<a href="#" onClick={(e) => { e.preventDefault(); downloadDisplayDocument(v); }}>download</a></>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              {head.url && (
                <div className="docactions">
                  <a href="#" onClick={(e) => { e.preventDefault(); showDocument(head); }}>
                    {previewBusy === head.url ? 'Opening…' : 'View'}
                  </a>
                  <a href="#" onClick={(e) => { e.preventDefault(); downloadDisplayDocument(head); }}>Download</a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card official-documents" data-testid="official-documents">
        <h2>Official program documents</h2>
        <section className="official-document-group" aria-labelledby="ds2019-heading">
          <h3 id="ds2019-heading">DS-2019</h3>
          {ds2019 ? officialRow(ds2019) : <p className="sub muted">Your DS-2019 is not available yet.</p>}
        </section>
        <section className="official-document-group" aria-labelledby="agreements-heading" data-testid="signed-agreements">
          <h3 id="agreements-heading">Signed agreements</h3>
          {agreements.length ? agreements.map(officialRow) : <p className="sub muted">No signed agreements are available yet.</p>}
        </section>
        {otherOfficial.length > 0 && (
          <section className="official-document-group" aria-labelledby="other-official-heading">
            <h3 id="other-official-heading">Other official documents</h3>
            {otherOfficial.map(officialRow)}
          </section>
        )}
      </div>
    </>
  );
}

function Uploader({ appId, onDone }: { appId: string | number; onDone: () => void }) {
  const online = useOnline();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>();
  const [drag, setDrag] = useState(false);

  const handle = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setMsg(undefined);
    try {
      for (const file of Array.from(files)) {
        if (online) {
          await uploadDocument(appId, file, { documentType: guessType(file.name) });
          setMsg(`Uploaded ${file.name}.`);
        } else {
          await uploadQueue.enqueue(appId, file, { documentType: guessType(file.name) });
          setMsg(`${file.name} queued — it will upload when you're back online.`);
        }
      }
      onDone();
    } catch (e) {
      setMsg(`Upload failed: ${(e as Error).message}`);
      track('document_upload_failed', { error: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`card dropzone ${drag ? 'drag' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); void handle(e.dataTransfer.files); }}
    >
      <h2>Upload a document</h2>
      <p>PDF, JPEG, PNG or WebP up to 15MB. Photos are compressed on your device before upload.</p>
      <p style={{ marginTop: 12 }}>
        <button disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Uploading…' : 'Choose file'}</button>
      </p>
      <input ref={input} type="file" hidden multiple accept=".pdf,image/*" onChange={(e) => void handle(e.target.files)} />
      {msg && <p role="status" style={{ marginTop: 10 }}>{msg}</p>}
    </div>
  );
}

const prettyType = (t: string) => t.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '');
/**
 * Signing date of a canonical signed-agreement document. `createdAt` on that
 * row is the legal acceptance timestamp Globus copies from the acceptance —
 * not an upload date. Null/unparseable → empty string so the UI omits the
 * label rather than asserting a false date or "Invalid Date".
 */
export function formatSignedOn(iso?: string): string {
  const ms = parseStrictIsoTimestamp(iso);
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

const ISO_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Strict ISO-8601 timestamp parse. `Date.parse` alone is not enough for a
 * legal date: it accepts bare numbers ("0" → 2000) and silently rolls
 * impossible calendar dates ("2026-02-30" → 2 March). Require the full
 * timestamp shape and verify the calendar components round-trip exactly.
 */
function parseStrictIsoTimestamp(value?: string): number | null {
  if (!value) return null;
  const m = ISO_TIMESTAMP.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number) as unknown as [string, number, number, number, number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || h > 23 || mi > 59 || s > 59) return null;
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}
const guessType = (name: string) => (/passport/i.test(name) ? 'passport' : /photo|\.jpe?g|\.png/i.test(name) ? 'photo' : 'general');
